import { useTranslation } from '@lichtblick/i18next';
import {
  AccessTime,
  BackHandOutlined,
  CancelOutlined,
  CheckCircleOutline,
  Close,
  Handyman,
  KeyboardDoubleArrowUp,
  PauseCircleOutline,
  RestartAlt,
  UndoOutlined,
} from '@lichtblick/icons';
import { TextVariants } from '@lichtblick/theme';
import { ChipSizes, ChipVariants, Counter, ApprovalState, AppointmentStatus } from '@lichtblick/types';

import { RootContainer, BaseChip, CloseContainer, TextContainer, CounterContainer } from './Chip.styles';

import { Text } from '../Text';

export type ChipProps = {
  counter?: Counter;
  isHighlighted?: boolean;
  onClick?: (link: string) => void;
  onCloseClick?: () => void;
  shouldShowIcon?: boolean;
  size: ChipSizes;
  text?: string;
  variant: ChipVariants;
};

const variantMap = (variant: ChipVariants) => {
  switch (variant) {
    case AppointmentStatus.Open:
    case AppointmentStatus.Scheduled:
      return { icon: <AccessTime /> };
    case AppointmentStatus.Started:
      return { icon: <Handyman /> };
    case AppointmentStatus.Aborted:
      return { icon: <CancelOutlined /> };
    case AppointmentStatus.Paused:
      return { icon: <PauseCircleOutline /> };
    case ApprovalState.ServerApproved:
    case ApprovalState.ClientApproved:
    case AppointmentStatus.Done:
      return { icon: <CheckCircleOutline /> };
    case ApprovalState.Pending:
      return { icon: <KeyboardDoubleArrowUp /> };
    case ApprovalState.Resubmitted:
      return { icon: <RestartAlt /> };
    case ApprovalState.Rejected:
      return { icon: <BackHandOutlined /> };
    case ApprovalState.Undoed:
      return { icon: <UndoOutlined /> };
    default:
      return {
        icon: undefined,
      };
  }
};

export const Chip = ({
  counter,
  isHighlighted = false,
  onClick,
  onCloseClick,
  shouldShowIcon = true,
  size = 'large',
  text,
  variant,
}: ChipProps) => {
  const { t } = useTranslation('glossary');

  if (!variant || variant === undefined) {
    return <></>;
  }

  const map = variantMap(variant);
  const isDescriptive = variant?.includes('descriptive');

  return (
    <RootContainer>
      <BaseChip $isHighlighted={isHighlighted} $size={size} $variant={variant} data-testid="base-chip-container">
        <TextContainer
          $isDescriptive={isDescriptive}
          $shouldShowIcon={shouldShowIcon}
          $size={size}
          onClick={() => onClick?.(counter?.link ?? '')}
        >
          {shouldShowIcon && map?.icon}
          <Text $variant={size === 'large' ? TextVariants.button.m : TextVariants.button.s}>
            {text ?? t(`glossary:workStatus.${variant.toLowerCase()}`)}
          </Text>
        </TextContainer>
        {isDescriptive && shouldShowIcon && (
          <CloseContainer $size={size} data-testid="chip-close-container" onClick={() => onCloseClick?.()}>
            <Close />
          </CloseContainer>
        )}
      </BaseChip>
      {counter && counter.count > 0 && (
        <CounterContainer
          $size={size}
          $variant={variant}
          data-testid="chip-counter-container"
          onClick={() => onClick && counter.link && onClick(counter.link)}
        >
          <Text $variant={size === 'large' ? TextVariants.button.m : TextVariants.button.s}>{counter.count}</Text>
        </CounterContainer>
      )}
    </RootContainer>
  );
};
